import loadable from '@loadable/component'
import { matchRoutes, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useLayoutEffect, useMemo } from 'react'

import TripGuard from 'wishlists/components/WishlistGuard'
import RootWrapper from './pages/RootWrapper'
import Trip404Page from './pages/Trip404Page'
import TripTabRedirect from './pages/TripTabRedirect'

import AuthRouteElement from 'components/App/Routes/AuthRouteElement'
import Redirect from 'tripPlanner/components/Redirect'
import { CHAT_TRIP_PLANNER_PATH } from 'tripPlanner/config'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useAppDispatch } from 'hooks/reduxHooks'
import { registerPageView } from 'actions/AnalyticsActions'

const LegacyTripArchivePage = loadable(() => import('tripPlanner/legacyArchive/LegacyTripArchivePage'))

const ChatTripPlannerPage = loadable(
  () =>
    import(
      'tripPlanner/pages/chat/ChatTripPlannerPage'
    ),
)

const tripTabs: Array<string> = ['summary', 'itinerary', 'explore', 'documents']
const publicTripTabs = ['summary', 'itinerary']

function isTripTab(tab: string | undefined): boolean {
  return !!tab && tripTabs.includes(tab.toLowerCase())
}

function isPublicTripTab(tab: string | undefined): boolean {
  return !!tab && publicTripTabs.includes(tab.toLowerCase())
}

function TripRouteGuard() {
  const { tab } = useParams<'tab'>()

  if (!isTripTab(tab)) {
    return <Trip404Page />
  }

  return <LegacyTripArchivePage />
}

function PublicTripRouteGuard() {
  const { tab } = useParams<'tab'>()

  if (!isPublicTripTab(tab)) {
    return <Trip404Page />
  }

  return <LegacyTripArchivePage isPublic />
}

const tripPlannerRoutes = [
  { path: '/:regionCode/trip-planner' },
  { path: '/:regionCode/trip-planner/generate-trip' },
  { path: '/:regionCode/trip-planner/chat-trip-planner/:chatId?' },
  { path: '/:regionCode/trip-planner/trip/:tripId/:tab' },
  { path: '/:regionCode/trip-planner/public-trip/:tripId/:tab' },
  { path: '/:regionCode/trip-planner/trip/:tripId/accept-invite/:inviteId' },
  { path: '/:regionCode/trip-planner/hidden-settings' },
  { path: '/:regionCode/trip-planner/webView/*' },
  { path: '/:regionCode/trip-planner/trip/:tripId' },
]

function TripPlannerRoutes() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => {
    return {
      ...location,
      pathname: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location])
  const matchedRoute = useMemo(() => {
    return matchRoutes(tripPlannerRoutes, localeStrippedLocation)?.at(-1)
  }, [localeStrippedLocation])

  useLayoutEffect(() => {
    if (!matchedRoute) return

    dispatch(registerPageView({
      template: matchedRoute.route.path,
      params: matchedRoute.params,
      path: matchedRoute.pathname,
      search: location.search,
    }))
  }, [dispatch, location.search, matchedRoute])

  return (
    <TripGuard fallback={<Trip404Page />}>
      <RootWrapper>
        <Routes location={localeStrippedLocation}>
          <Route path="about" element={<Redirect to={CHAT_TRIP_PLANNER_PATH} />} />
          <Route index element={<Redirect to={CHAT_TRIP_PLANNER_PATH} />} />
          <Route path="generate-trip" element={<ChatTripPlannerPage />} />
          <Route path="chat-trip-planner/:chatId?" element={<ChatTripPlannerPage />} />
          <Route
            path="trip/:tripId/:tab"
            element={(
              <AuthRouteElement showPopup path="/:regionCode/trip-planner/trip/:tripId/:tab">
                <TripRouteGuard />
              </AuthRouteElement>
            )}
          />
          <Route
            path="public-trip/:tripId/:tab"
            element={<PublicTripRouteGuard />}
          />
          <Route
            path="trip/:tripId/accept-invite/:inviteId"
            element={(
              <AuthRouteElement showPopup path="/:regionCode/trip-planner/trip/:tripId/accept-invite/:inviteId">
                <LegacyTripArchivePage />
              </AuthRouteElement>
            )}
          />
          <Route
            path="hidden-settings"
            element={(
              <AuthRouteElement showPopup path="/:regionCode/trip-planner/hidden-settings">
                <Redirect to={CHAT_TRIP_PLANNER_PATH} />
              </AuthRouteElement>
            )}
          />
          <Route path="webView/saved" element={<Redirect to="/wishlists" />} />
          <Route
            path="webView/:tripId/*"
            element={(
              <AuthRouteElement showPopup path="/:regionCode/trip-planner/webView/:tripId/*">
                <LegacyTripArchivePage />
              </AuthRouteElement>
            )}
          />
          <Route
            path="trip/:tripId"
            element={<TripTabRedirect />}
          />
          <Route path="*" element={<Trip404Page />} />
        </Routes>
      </RootWrapper>
    </TripGuard>
  )
}

export default TripPlannerRoutes
